import React, { createContext, useCallback, useContext, useReducer } from 'react';
import { ITabItemProps, ITabData } from './tab-types';

interface ITabContext {
    addItem(props: ITabItemProps): void;
    removeItem(id: React.ReactText): void;
}

const NOOP = () => { /** No-Operation Function */ };

export const TabContext = createContext<ITabContext>({
    addItem: NOOP,
    removeItem: NOOP
});

type DataTabActions = {
    action: 'CLEAR',
    key?: React.Key;
} | {
    action: 'UPSERT',
    key: React.Key,
    data: any
};

interface ITabDataContext {
    data: ITabData;
    dispatch: React.Dispatch<DataTabActions>;
}

const TabDataContext = createContext<ITabDataContext>({
    data: {},
    dispatch: NOOP
});

interface ITabDataProviderProps {
    initialTabData?: ITabData;
    children: React.ReactNode | React.ReactNode[];
}


function tabDabReducer(state: ITabData, payload: DataTabActions) {
    switch (payload.action) {
        case 'CLEAR':
            if (payload.key !== undefined) {
                delete state[payload.key];
                return { ...state };
            }
            return {};
        case 'UPSERT':
            return {
                ...state,
                [payload.key]: payload.data
            };
        default:
            return state;
    }
}

export function TabDataProvider(props: ITabDataProviderProps) {
    const { children, initialTabData = {} } = props;
    const [data, dispatch] = useReducer(tabDabReducer, initialTabData);
    return (
        <TabDataContext.Provider value={{ data, dispatch }}>
            {children}
        </TabDataContext.Provider>
    );
}

export function useTabData() {
    const { data, dispatch } = useContext(TabDataContext);
    const id = useTabId();

    const setTabData = useCallback((data: any) => {
        dispatch({
            action: 'UPSERT',
            key: id,
            data
        });
    }, [dispatch, id]);

    const clearTabData = useCallback(() => {
        dispatch({
            action: 'CLEAR',
            key: id
        })
    }, [dispatch, id]);

    return { tabData: data[id], setTabData, clearTabData };
}

const TabIdContext = createContext<React.Key>('');

interface ITabIdProviderProps {
    id: React.Key;
    children: React.ReactNode | React.ReactNode[];
}

export function TabIdProvider(props: ITabIdProviderProps) {
    const { children, id } = props;
    return (
        <TabIdContext.Provider value={id}>
            {children}
        </TabIdContext.Provider>
    );
}

function useTabId() {
    return useContext(TabIdContext);
}
